import type { LDFlagValue } from 'public/assets/js/analytics/launchDarkly';
import { useLD } from 'src/contexts/LaunchDarklyContext/LaunchDarklyContext';
import { contactRoutingConfig } from './config';
import {
  type Eligibility,
  type ExclusionReason,
  useContactRoutingEligibility,
} from './use-contact-routing-eligibility';

const { flag } = contactRoutingConfig;

const ARMS: ReadonlyArray<string> = [flag.control, flag.variant];

export type ContactRoutingOutcome =
  | { type: 'none' }
  | { type: 'exposed'; arm: string }
  | { type: 'excluded'; arm: string; reason: ExclusionReason | 'not-routed' };

export const getAssignedContactRoutingArm = (
  value: LDFlagValue,
): string | null => {
  return typeof value === 'string' && ARMS.includes(value) ? value : null;
};

export const getContactRoutingOutcome = (
  value: LDFlagValue,
  eligibility: Eligibility,
  arm: string,
): ContactRoutingOutcome => {
  const assigned = getAssignedContactRoutingArm(value);

  if (!assigned || eligibility.status === 'pending') {
    return { type: 'none' };
  }
  if (eligibility.status === 'excluded') {
    return { type: 'excluded', arm: assigned, reason: eligibility.reason };
  }
  return assigned === arm
    ? { type: 'exposed', arm: assigned }
    : { type: 'excluded', arm: assigned, reason: 'not-routed' };
};

export const useContactRoutingFlagValue = (): LDFlagValue => {
  return useLD()?.flags[flag.key] ?? null;
};

export const useContactRoutingOutcome = (
  arm: string,
): ContactRoutingOutcome => {
  return getContactRoutingOutcome(
    useContactRoutingFlagValue(),
    useContactRoutingEligibility(),
    arm,
  );
};
